import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { INCIDENT_TYPES } from './incidentConstants';
import { InjurySection } from './InjurySection';
import { WitnessesSection } from './WitnessesSection';
import { PhotoPicker } from './PhotoPicker';
import { SignaturePad } from '../SignaturePad';
import { fmtTz } from "../../lib/time";

const emptyForm = (name) => ({
  incident_type: '', incident_date: fmtTz(new Date(), 'yyyy-MM-dd'), incident_time: fmtTz(new Date(), 'HH:mm'),
  location: '', project_id: '', description: '', immediate_actions: '',
  injury_occurred: false, injured_person_name: '', injured_person_role: '', body_parts: '', injury_description: '', treatment: '',
  witnesses: [], signed_name: name || '', signature_data: null,
});

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h3 className="font-semibold text-stone-800 border-b border-stone-200 pb-2">{title}</h3>
    {children}
  </div>
);

export const IncidentForm = ({ onSubmitted }) => {
  const { authAxios, user } = useAuth();
  const [form, setForm] = useState(emptyForm(user?.full_name));
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [coords, setCoords] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    authAxios.get('/projects?active_only=true').then((r) => setProjects(r.data)).catch(() => {});
    navigator.geolocation?.getCurrentPosition((p) => setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude }), () => {});
  }, [authAxios]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.incident_type || !form.location.trim() || !form.description.trim()) return toast.error('Please fill in the type, location and what happened');
    if (form.injury_occurred && !form.injured_person_name.trim()) return toast.error("Please enter the injured person's name");
    if (!form.signature_data || !form.signed_name.trim()) return toast.error('Please type your name and sign the report');
    setSubmitting(true);
    try {
      const payload = {
        ...form, ...(coords || {}),
        project_id: form.project_id && form.project_id !== 'none' ? form.project_id : null,
        witnesses: form.witnesses.filter((w) => w.name.trim()),
      };
      const { data } = await authAxios.post('/incident-reports', payload);
      let failed = 0;
      for (const photo of photos) {
        const fd = new FormData();
        fd.append('file', photo);
        await authAxios.post(`/incident-reports/${data.id}/photos`, fd).catch(() => { failed += 1; });
      }
      if (failed) toast.warning(`Report submitted, but ${failed} photo(s) failed to upload`);
      else toast.success('Incident report submitted');
      onSubmitted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="incident-form">
      <Section title="What happened">
        <div className="space-y-2">
          <Label>Type of incident *</Label>
          <Select value={form.incident_type} onValueChange={(v) => set('incident_type', v)}>
            <SelectTrigger data-testid="incident-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>
              {INCIDENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input type="date" value={form.incident_date} onChange={(e) => set('incident_date', e.target.value)} data-testid="incident-date" />
          </div>
          <div className="space-y-2">
            <Label>Time *</Label>
            <Input type="time" value={form.incident_time} onChange={(e) => set('incident_time', e.target.value)} data-testid="incident-time" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Project / site</Label>
          <Select value={form.project_id} onValueChange={(v) => set('project_id', v)}>
            <SelectTrigger data-testid="incident-project-select"><SelectValue placeholder="Select site (optional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No site / on the road</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Exact place *</Label>
          <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. north gate, loading bay" data-testid="incident-location" />
          {coords && <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="incident-gps-attached"><MapPin className="w-3 h-3" /> Your GPS location will be attached</p>}
        </div>
        <div className="space-y-2">
          <Label>Describe what happened *</Label>
          <Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="incident-description" />
        </div>
        <div className="space-y-2">
          <Label>Actions taken right away</Label>
          <Textarea rows={2} value={form.immediate_actions} onChange={(e) => set('immediate_actions', e.target.value)} data-testid="incident-actions" />
        </div>
      </Section>
      <Section title="Injury"><InjurySection form={form} set={set} /></Section>
      <Section title="Witnesses & photos">
        <WitnessesSection witnesses={form.witnesses} onChange={(w) => set('witnesses', w)} />
        <PhotoPicker photos={photos} onChange={setPhotos} />
      </Section>
      <Section title="Sign off">
        <div className="space-y-2">
          <Label>Your full name *</Label>
          <Input value={form.signed_name} onChange={(e) => set('signed_name', e.target.value)} data-testid="incident-signed-name" />
        </div>
        <SignaturePad onChange={(d) => set('signature_data', d)} />
      </Section>
      <Button type="submit" disabled={submitting} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="incident-submit-btn">
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Submit Report
      </Button>
    </form>
  );
};
